import { useState } from "react";
import { FeedbackToggle } from "@volca/ui/components/ui/feedback-toggle";
import { toast } from "@volca/ui/lib/toast";
import { reportError } from "@/profitability/utils";
import {
  FeedbackDialog,
  findingDownReasons,
  INSIGHT_DOWN_REASONS,
} from "./feedback-dialog";
import { useRate } from "./query";
import type { FeedbackSummary } from "./types";

/**
 * Thumbs on a finding or an insight. Up saves at once; down asks why first,
 * because the reason is what the rating teaches.
 */
export const Rating = ({
  businessId,
  subjectType,
  subjectId,
  runId,
  feedback,
  label,
  noun,
}: {
  businessId: string;
  subjectType: "finding" | "insight";
  subjectId: string;
  /** The run a finding was shown for; its figures are what the rating keeps. */
  runId?: string | null;
  feedback: FeedbackSummary | undefined;
  /** What is rated, for the buttons' names: "job 48425". */
  label: string;
  /** What a rated finding is, lower case: "job". Says "work" without it. */
  noun?: string;
}) => {
  const rate = useRate(businessId);
  const [asking, setAsking] = useState(false);
  const save = (rating: "up" | "down" | null, reason?: string, note?: string) =>
    rate.mutate(
      { subjectType, subjectId, runId, rating, reason, note },
      {
        onSuccess: () => setAsking(false),
        onError: (error) =>
          toast.error("The rating could not be saved", {
            description: reportError(error),
          }),
      },
    );
  return (
    <>
      <FeedbackToggle
        size="sm"
        className="pointer-coarse:[&_button]:min-h-11 pointer-coarse:[&_button]:min-w-11"
        upLabel={`Worth a look: ${label}`}
        downLabel={`Not worth a look: ${label}`}
        upCount={feedback?.up}
        downCount={feedback?.down}
        value={feedback?.mine ?? null}
        disabled={rate.isPending}
        onValueChange={(next) =>
          next === "down" ? setAsking(true) : save(next)
        }
        onClick={(event) => event.stopPropagation()}
      />
      <FeedbackDialog
        open={asking}
        onOpenChange={setAsking}
        title={
          subjectType === "finding"
            ? "Why isn’t this worth a look?"
            : "What’s wrong with this?"
        }
        reasons={
          subjectType === "finding"
            ? findingDownReasons(noun)
            : INSIGHT_DOWN_REASONS
        }
        submitLabel="Send"
        pending={rate.isPending}
        onSubmit={({ reason, note }) => save("down", reason, note || undefined)}
      />
    </>
  );
};
